import { useCallback, useState } from 'react';
import QRCode from 'qrcode';
import { Button } from '@/components/Button';
import type { ButtonSize, ButtonVariant } from '@/components/Button';
import { IconDownload } from '@/components/Icons';

export interface QrDownloadButtonProps {
  /** What the code points at. */
  url: string;
  /** Saved file name, without extension — a .png is appended. */
  filename: string;
  label?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
}

/**
 * Generates the QR code entirely in the browser — the URL is already public,
 * so there is nothing for a server round trip to protect, and no export
 * pipeline to build just to turn a string into a PNG.
 */
export function QrDownloadButton({
  url,
  filename,
  label = 'Download barcode',
  variant = 'secondary',
  size = 'sm',
}: QrDownloadButtonProps) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleClick = useCallback(async () => {
    setBusy(true);
    setFailed(false);
    try {
      const dataUrl = await QRCode.toDataURL(url, { width: 640, margin: 2 });
      const anchor = document.createElement('a');
      anchor.href = dataUrl;
      anchor.download = `${filename}.png`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }, [filename, url]);

  return (
    <div className="inline-flex flex-col items-start gap-1">
      <Button
        variant={variant}
        size={size}
        loading={busy}
        onClick={() => void handleClick()}
        icon={<IconDownload className="h-3.5 w-3.5" />}
      >
        {label}
      </Button>
      {failed ? <p className="text-xs text-rose-700">Could not generate the code. Try again.</p> : null}
    </div>
  );
}
